// canvas/annotations.js: Pillar 5 bot reasoning callouts (M2.c-9).
//
// Renders semi-transparent dark callout adjacent to a bot's nameplate.
// Positioning depends on seat align:
//   bottom seat → callout ABOVE the plate
//   top seat    → callout BELOW the plate
//   left seat   → callout to the RIGHT (inner side)
//   right seat  → callout to the LEFT (inner side)
//
// Caller supplies the per-frame alpha (0..1) from annotation-logic so
// the renderer stays pure, no time math inside the canvas pass.
//
// Text is truncated to fit the callout width. The callout itself caps
// at 220px desktop / 160px mobile so a verbose annotation can't push
// off the canvas.

const COLOR_BG_BASE   = 'rgba(30, 20, 40, ALPHA)';  // alpha gets baked
const COLOR_BORDER    = 'rgba(154, 138, 184, ALPHA)';
const COLOR_TEXT      = 'rgba(245, 245, 240, ALPHA)';
const PAD_X = 8;
const PAD_Y = 6;
const GAP   = 8;  // between plate and callout
const MAX_W_DESKTOP = 220;
const MAX_W_MOBILE  = 160;
const FONT_DESKTOP  = '600 12px ui-monospace, SFMono-Regular, Menlo, monospace';
const FONT_MOBILE   = '600 11px ui-monospace, SFMono-Regular, Menlo, monospace';

function rgba(template, alpha) {
  return template.replace('ALPHA', String(Math.max(0, Math.min(1, alpha))));
}

function fillRoundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y,     x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x,     y + h, r);
  ctx.arcTo(x,     y + h, x,     y,     r);
  ctx.arcTo(x,     y,     x + w, y,     r);
  ctx.closePath();
  ctx.fill();
}

function strokeRoundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y,     x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x,     y + h, r);
  ctx.arcTo(x,     y + h, x,     y,     r);
  ctx.arcTo(x,     y,     x + w, y,     r);
  ctx.closePath();
  ctx.stroke();
}

/**
 * Truncate `text` to fit `maxTextW` in the current font. Appends "…"
 * when truncation occurs. Cheap measureText loop, fine for short
 * annotation strings (< 50 chars typical).
 */
function truncateToFit(ctx, text, maxTextW) {
  if (!text) return '';
  if (ctx.measureText(text).width <= maxTextW) return text;
  const ellipsis = '…';
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    const t = text.slice(0, mid) + ellipsis;
    if (ctx.measureText(t).width <= maxTextW) lo = mid;
    else hi = mid - 1;
  }
  if (lo === 0) return ellipsis;
  return text.slice(0, lo) + ellipsis;
}

/**
 * Greedy word-wrap into at most two lines for the bid-state variant
 * (T10): bid reasoning is the one annotation worth reading in full,
 * and one truncated line kept cutting it mid-thought. The second line
 * still ellipsizes if the text is truly enormous.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} text
 * @param {number} maxTextW
 * @returns {string[]} 1 or 2 lines
 */
function wrapTwoLines(ctx, text, maxTextW) {
  if (!text) return [];
  if (ctx.measureText(text).width <= maxTextW) return [text];
  const words = text.split(/\s+/);
  let first = '';
  let i = 0;
  for (; i < words.length; i++) {
    const candidate = first ? `${first} ${words[i]}` : words[i];
    if (ctx.measureText(candidate).width > maxTextW && first) break;
    first = candidate;
  }
  const rest = words.slice(i).join(' ');
  if (!rest) return [first];
  return [first, truncateToFit(ctx, rest, maxTextW)];
}

/**
 * Compute the (x, y, w, h) of the callout for a given seat. Pure,
 * exported so tests can verify positioning without a canvas context.
 *
 * @param {{id:1|2|3|4,align:'bottom'|'left'|'top'|'right',x:number,y:number,w:number,h:number}} seat
 * @param {import('./layout.js').TableLayout} layout
 * @param {number} contentW  measured + padded width of the text box
 * @returns {{x:number, y:number, w:number, h:number}}
 */
export function computeAnnotationBox(seat, layout, contentW, lines = 1) {
  const isMobile = !!layout.isMobile;
  const maxW = isMobile ? MAX_W_MOBILE : MAX_W_DESKTOP;
  const w = Math.min(maxW, Math.max(seat.w, contentW));
  const lineH = isMobile ? 16 : 18;
  const h = (isMobile ? 22 : 26) + PAD_Y + (Math.max(1, lines) - 1) * lineH;

  let x;
  let y;
  switch (seat.align) {
    case 'bottom':
      x = seat.x + Math.round((seat.w - w) / 2);
      y = seat.y - h - GAP;
      break;
    case 'top':
      x = seat.x + Math.round((seat.w - w) / 2);
      y = seat.y + seat.h + GAP;
      break;
    case 'left':
      x = seat.x + seat.w + GAP;
      y = seat.y + Math.round((seat.h - h) / 2);
      break;
    case 'right':
    default:
      x = seat.x - w - GAP;
      y = seat.y + Math.round((seat.h - h) / 2);
      break;
  }
  // Clamp so the callout never spills past the canvas edges.
  const margin = 4;
  x = Math.max(margin, Math.min(layout.canvasW - margin - w, x));
  y = Math.max(margin, Math.min(layout.canvasH - margin - h, y));
  return { x, y, w, h };
}

/**
 * Render a single annotation callout. `alpha` controls the fade,
 * 0 means do nothing, 1 means fully opaque.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {{id:1|2|3|4,align:'bottom'|'left'|'top'|'right',x:number,y:number,w:number,h:number}} seat
 * @param {import('./layout.js').TableLayout} layout
 * @param {string} text
 * @param {number} alpha
 * @param {{twoLine?: boolean}} [opts]  twoLine: bid-state full-text mode
 */
export function renderAnnotation(ctx, seat, layout, text, alpha, opts = {}) {
  if (alpha <= 0 || !text) return;
  const isMobile = !!layout.isMobile;
  const font = isMobile ? FONT_MOBILE : FONT_DESKTOP;

  ctx.save();
  ctx.font = font;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';

  const maxW = isMobile ? MAX_W_MOBILE : MAX_W_DESKTOP;
  const maxTextW = maxW - PAD_X * 2;
  const lines = opts.twoLine
    ? wrapTwoLines(ctx, text, maxTextW)
    : [truncateToFit(ctx, text, maxTextW)];
  const measuredW = Math.max(...lines.map((l) => ctx.measureText(l).width)) + PAD_X * 2;
  const box = computeAnnotationBox(seat, layout, measuredW, lines.length);

  // Background.
  ctx.fillStyle = rgba(COLOR_BG_BASE, alpha * 0.85);
  fillRoundRect(ctx, box.x, box.y, box.w, box.h, 6);
  ctx.strokeStyle = rgba(COLOR_BORDER, alpha * 0.65);
  ctx.lineWidth = 1;
  strokeRoundRect(ctx, box.x + 0.5, box.y + 0.5, box.w - 1, box.h - 1, 6);

  // Text, vertically centered as a block.
  ctx.fillStyle = rgba(COLOR_TEXT, alpha);
  const lineH = isMobile ? 16 : 18;
  const blockTop = box.y + box.h / 2 - ((lines.length - 1) * lineH) / 2;
  lines.forEach((line, i) => {
    ctx.fillText(line, box.x + PAD_X, Math.round(blockTop + i * lineH));
  });

  ctx.restore();
}

/**
 * Convenience: render every active annotation in `annotations` keyed
 * by seat id. Each entry must carry `{text, alpha}`.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {import('./layout.js').TableLayout} layout
 * @param {Record<number, {text:string, alpha:number}>|null|undefined} annotations
 */
export function renderAnnotations(ctx, layout, annotations, opts = {}) {
  if (!annotations) return;
  for (const seat of layout.seats) {
    const a = annotations[seat.id];
    if (a && a.alpha > 0 && a.text) {
      renderAnnotation(ctx, seat, layout, a.text, a.alpha, opts);
    }
  }
}

export const _internals = {
  PAD_X, PAD_Y, GAP,
  MAX_W_DESKTOP, MAX_W_MOBILE,
  truncateToFit,
  wrapTwoLines,
};
